import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import client from "@/lib/api";

const REFRESH_MS = 30_000;

const fetchGlobal = () => client.get("/global-markets").then((r) => r.data);

const fmt = (n) => {
    if (n === null || n === undefined) return "—";
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Row = ({ idx }) => {
    if (!idx.ok) {
        return (
            <div className="flex items-center justify-between py-2.5 border-b border-white/8 last:border-b-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base leading-none">{idx.flag}</span>
                    <span className="font-heading text-[13px] font-semibold text-white/80 truncate">{idx.name}</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/40">n/a</span>
            </div>
        );
    }
    const up = idx.change_pct >= 0;
    const Icon = idx.change_pct > 0 ? TrendingUp : idx.change_pct < 0 ? TrendingDown : Minus;
    return (
        <div className="flex items-center justify-between py-2.5 border-b border-white/8 last:border-b-0 hover:bg-white/5 -mx-3 px-3 transition-colors">
            <div className="flex items-center gap-2 min-w-0">
                <span className="text-base leading-none shrink-0">{idx.flag}</span>
                <div className="min-w-0">
                    <div className="font-heading text-[13px] font-semibold text-white leading-tight truncate">{idx.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/40 leading-tight mt-0.5">{idx.currency}</div>
                </div>
            </div>
            <div className="text-right shrink-0 ml-2">
                <div className="font-mono text-[13px] text-white/95 tabular-nums">{fmt(idx.price)}</div>
                <div className={`font-mono text-[10.5px] tabular-nums flex items-center justify-end gap-0.5 ${up ? "text-emerald-400" : "text-rose-400"}`}>
                    <Icon className="w-2.5 h-2.5" />
                    {up ? "+" : ""}{fmt(idx.change_pct)}%
                </div>
            </div>
        </div>
    );
};

const Group = ({ title, items }) => {
    if (!items.length) return null;
    return (
        <div>
            <div className="font-sub text-[9px] uppercase tracking-[0.24em] text-white/40 mt-4 mb-1.5 first:mt-0">
                {title}
            </div>
            <div>
                {items.map((i) => <Row key={i.symbol} idx={i} />)}
            </div>
        </div>
    );
};

export default function GlobalMarkets({ fitViewport = false, testId = "global-markets" }) {
    const { data, isLoading, dataUpdatedAt } = useQuery({
        queryKey: ["global-markets"],
        queryFn: fetchGlobal,
        refetchInterval: REFRESH_MS,
        refetchIntervalInBackground: true,   // keep polling even when tab is inactive
        refetchOnWindowFocus: true,          // refetch when user returns to tab
        refetchOnReconnect: true,            // refetch on network reconnect
        staleTime: 0,                        // never consider prices "fresh enough" to skip a refresh
    });
    const indices = data?.indices || [];

    const groups = {
        "Gift Nifty · India": indices.filter((i) => i.region === "india"),
        "United States": indices.filter((i) => i.region === "us"),
        "Europe": indices.filter((i) => i.region === "europe"),
        "Asia · Pacific": indices.filter((i) => i.region === "asia"),
        "Crypto": indices.filter((i) => i.region === "crypto"),
    };
    const secs = dataUpdatedAt ? Math.max(0, Math.floor((Date.now() - dataUpdatedAt) / 1000)) : null;

    // When fitViewport is true, the card sizes to the viewport and the indices
    // list scrolls internally — used inside a sticky sidebar so the whole
    // panel stays visible while the user scrolls the main page.
    const containerClass = fitViewport
        ? "bg-[#0F172A] text-white border border-black/10 rounded-sm flex flex-col max-h-[calc(100vh-7rem)]"
        : "bg-[#0F172A] text-white border border-black/10 rounded-sm p-4";

    return (
        <div className={containerClass} data-testid={testId}>
            <div className={`flex items-center justify-between ${fitViewport ? "px-4 pt-4 pb-2 border-b border-white/10" : "mb-2"}`}>
                <div>
                    <div className="font-sub text-[10px] uppercase tracking-[0.22em] text-white/60">Live · Global Markets</div>
                    <div className="font-heading font-bold text-lg leading-tight mt-1">Market Status</div>
                </div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] text-white/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {secs === null ? "…" : `${secs}s`}
                </div>
            </div>
            {isLoading ? (
                <div className="py-6 text-center text-white/40 font-mono text-xs">Loading indices…</div>
            ) : (
                <div
                    className={fitViewport ? "overflow-y-auto flex-1 px-4 gm-scroll" : "space-y-0"}
                    data-testid={`${testId}-list`}
                >
                    {Object.entries(groups).map(([label, items]) => (
                        <Group key={label} title={label} items={items} />
                    ))}
                </div>
            )}
            <div className={`font-sub text-[9px] uppercase tracking-[0.2em] text-white/35 border-t border-white/10 ${fitViewport ? "px-4 py-2.5" : "mt-3 pt-2.5"}`}>
                Prices via Yahoo Finance · refresh 45s
            </div>
        </div>
    );
}
